/**
 * Async utilities
 */

export type AsyncFunction = () => Promise<any>;

export async function parallel(...fns: Array<Promise<any> | AsyncFunction>): Promise<any[]> {
  // Wrap each function in a Promise, if it's not already a Promise
  const promises = fns.map((fn) => (fn instanceof Promise ? fn : fn()));

  // Wait for all Promises to resolve
  return await Promise.all(promises);
}

/**
 * Debounce a function by the given number of milliseconds
 */
export function debounce<T extends (...args: any[]) => any>(fn: T, ms: number): T {
  let timeout: NodeJS.Timeout | undefined;

  return function (...args: any[]) {
    clearTimeout(timeout!);
    timeout = setTimeout(() => fn(...args), ms);
  } as any;
}

/**
 * Debounce a function with leading edge execution
 * Runs immediately on first call, then prevents subsequent calls for the given duration
 */
export function debounceLeading<T extends (...args: any[]) => any>(fn: T, ms: number): T {
  let blocked = false;

  return function (...args: any[]) {
    if (blocked) return;

    fn(...args);
    blocked = true;
    setTimeout(() => {
      blocked = false;
    }, ms);
  } as any;
}

export function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface WithRetryOnErrorOptions<T> {
  /** Max attempts including the first (default 4). */
  maxAttempts?: number;
  /**
   * Delay before each retry after the first attempt: `baseDelayMs * attemptIndex`
   * (default 300 → 300ms, 600ms, 900ms, …).
   */
  baseDelayMs?: number;
  /** Return true to retry after a delay. Receives the last value returned by `run()`. */
  shouldRetry: (result: T) => boolean;
}

/**
 * Re-runs `run()` when `shouldRetry` returns true for its result, with linear backoff.
 */
export async function withRetryOnError<T>(
  run: () => Promise<T>,
  options: WithRetryOnErrorOptions<T>,
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? 4;
  const baseDelayMs = options.baseDelayMs ?? 300;
  let last: T | undefined;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await wait(baseDelayMs * attempt);
    }
    last = await run();
    if (!options.shouldRetry(last)) {
      return last;
    }
  }
  return last!;
}
